import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { Button } from '$lib/components/ui/button';
import { Input } from '$lib/components/ui/input';
import * as Card from '$lib/components/ui/card';
import { Label } from '$lib/components/ui/label';
import { createSplit, getSplit } from '$lib/api/splits';
import { addToast } from '$lib/stores/toastStore.svelte';
import { navigate } from '$lib/router';
import { saveLastSplit, loadLastSplits, removeLastSplit } from '$lib/stores/lastSplitStore';
import { hasValidToken, loadToken, clearToken, CAPTCHA_TOKEN_HEADER } from '$lib/stores/captchaStore';
import CaptchaModal from '$lib/components/ui/captcha/CaptchaModal.svelte';

var root_6 = $.from_html(`<p id="splitName-error" class="text-sm text-destructive"> </p>`);
var root_8 = $.from_svg(`<svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Creating...`, 1);
var root_4 = $.from_html(`<form class="space-y-4"><div class="space-y-2"><!> <!> <!></div> <!></form>`);
var root_1 = $.from_html(`<!> <!>`, 1);
var root_15 = $.from_html(`<div class="flex items-center gap-3 min-h-[44px]"><span class="flex-1 min-w-0 truncate text-sm font-medium text-foreground"> </span> <!> <button class="shrink-0 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground">Dismiss</button></div>`);
var root_11 = $.from_html(`<!> <!>`, 1);
var root_10 = $.from_html(`<div class="w-full max-w-[520px]"><!></div>`);
var root_17 = $.from_html(`<section class="w-full max-w-[520px] border border-dashed border-orange-300 rounded-lg p-3 space-y-1"><p class="text-xs font-semibold text-orange-500 uppercase tracking-wide">Dev tools</p> <button class="text-xs text-orange-600 underline underline-offset-2 hover:text-orange-800">Clear CAPTCHA token</button></section>`);
var root = $.from_html(`<!> <div class="flex flex-col items-center space-y-6"><header class="text-center py-8"><h1 class="text-2xl font-bold text-primary">FairNSquare</h1> <p class="text-muted-foreground mt-2">Split expenses fairly with friends</p></header> <div class="w-full max-w-[520px]"><!></div> <!> <section class="text-center text-muted-foreground text-sm max-w-[520px]"><p>No account needed. Create a split and share the link!</p></section> <!></div>`, 1);

export default function Home($$anchor, $$props) {
	$.push($$props, true);

	// Home page - Create Split
	// Issue #94: Rework welcome page — list of recent splits, no first-participant form
	// CAPTCHA state — shown when user clicks Create Split without a valid token
	let showCaptcha = $.state(false);

	function handleCaptchaSuccess() {
		$.set(showCaptcha, false);
		doCreateSplit();
	}

	// Recent splits state
	let recentSplits = $.state($.proxy([]));

	$.user_effect(() => {
		const stored = loadLastSplits();

		if (stored.length === 0) return;

		Promise.allSettled(stored.map((s) => getSplit(s.id))).then((results) => {
			const verified = [];

			results.forEach((result, i) => {
				if (result.status === 'fulfilled') {
					verified.push({ id: result.value.id, name: result.value.name });
				} else {
					removeLastSplit(stored[i].id);
				}
			});

			$.set(recentSplits, verified, true);
		});
	});

	function handleResume(id) {
		navigate('/splits/:splitId', { params: { splitId: id } });
	}

	function handleDismiss(id) {
		removeLastSplit(id);
		$.set(recentSplits, $.get(recentSplits).filter((s) => s.id !== id), true);
	}

	// Form state
	let splitName = $.state('');

	let isLoading = $.state(false);
	let splitNameTouched = $.state(false);

	let splitNameError = $.derived(() => {
		if (!$.get(splitNameTouched)) return null;
		if (!$.get(splitName).trim()) return 'Split name is required';
		if ($.get(splitName).length > 100) return 'Split name cannot exceed 100 characters';

		return null;
	});

	let isValid = $.derived(() => $.get(splitName).trim().length > 0 && $.get(splitName).length <= 100);

	function handleCreateSplit() {
		$.set(splitNameTouched, true);

		if (!$.get(isValid)) return;

		if (!hasValidToken()) {
			$.set(showCaptcha, true);

			return;
		}

		doCreateSplit();
	}

	async function doCreateSplit() {
		$.set(isLoading, true);

		try {
			const token = loadToken();

			if (!token) {
				$.set(showCaptcha, true);

				return;
			}

			const split = await createSplit({ name: $.get(splitName).trim() }, { [CAPTCHA_TOKEN_HEADER]: token });

			saveLastSplit({ id: split.id, name: split.name });
			navigate('/splits/:splitId/participants', { params: { splitId: split.id } });
		} catch(err) {
			const apiError = err;

			if (apiError.status === 401) {
				clearToken();
				$.set(showCaptcha, true);
			} else {
				addToast({
					type: 'error',
					message: apiError.detail || 'Failed to create split'
				});
			}
		} finally {
			$.set(isLoading, false);
		}
	}

	var fragment = root();
	var node = $.first_child(fragment);

	CaptchaModal(node, {
		get open() {
			return $.get(showCaptcha);
		},

		onSuccess: handleCaptchaSuccess
	});

	var div = $.sibling(node, 2);
	var div_1 = $.sibling($.child(div), 2);
	var node_1 = $.child(div_1);

	$.component(node_1, () => Card.Root, ($$anchor, Card_Root) => {
		Card_Root($$anchor, {
			children: ($$anchor, $$slotProps) => {
				var fragment_1 = root_1();
				var node_2 = $.first_child(fragment_1);

				$.component(node_2, () => Card.Header, ($$anchor, Card_Header) => {
					Card_Header($$anchor, {
						children: ($$anchor, $$slotProps) => {
							var fragment_2 = $.comment();
							var node_3 = $.first_child(fragment_2);

							$.component(node_3, () => Card.Title, ($$anchor, Card_Title) => {
								Card_Title($$anchor, {
									children: ($$anchor, $$slotProps) => {
										$.next();

										var text = $.text('Create a New Split');

										$.append($$anchor, text);
									},

									$$slots: { default: true }
								});
							});

							$.append($$anchor, fragment_2);
						},

						$$slots: { default: true }
					});
				});

				var node_4 = $.sibling(node_2, 2);

				$.component(node_4, () => Card.Content, ($$anchor, Card_Content) => {
					Card_Content($$anchor, {
						children: ($$anchor, $$slotProps) => {
							var form = root_4();
							var div_2 = $.child(form);
							var node_5 = $.child(div_2);

							Label(node_5, {
								for: 'splitName',

								children: ($$anchor, $$slotProps) => {
									$.next();

									var text_1 = $.text('Split Name');

									$.append($$anchor, text_1);
								},

								$$slots: { default: true }
							});

							var node_6 = $.sibling(node_5, 2);

							{
								let $0 = $.derived(() => $.get(splitNameError) ? 'true' : undefined);
								let $1 = $.derived(() => $.get(splitNameError) ? 'splitName-error' : undefined);

								Input(node_6, {
									type: 'text',
									id: 'splitName',

									onblur: () => {
										$.set(splitNameTouched, true);
									},

									oninput: () => {
										$.set(splitNameTouched, true);
									},

									placeholder: 'e.g., Weekend Trip',

									get disabled() {
										return $.get(isLoading);
									},

									class: 'min-h-[44px]',

									get 'aria-invalid'() {
										return $.get($0);
									},

									get 'aria-describedby'() {
										return $.get($1);
									},

									get value() {
										return $.get(splitName);
									},

									set value($$value) {
										$.set(splitName, $$value, true);
									}
								});
							}

							var node_7 = $.sibling(node_6, 2);

							{
								var consequent = ($$anchor) => {
									var p = root_6();
									var text_2 = $.child(p, true);

									$.reset(p);
									$.template_effect(() => $.set_text(text_2, $.get(splitNameError)));
									$.append($$anchor, p);
								};

								$.if(node_7, ($$render) => {
									if ($.get(splitNameError)) $$render(consequent);
								});
							}

							$.reset(div_2);

							var node_8 = $.sibling(div_2, 2);

							Button(node_8, {
								type: 'submit',

								get disabled() {
									return $.get(isLoading);
								},

								class: 'w-full min-h-[44px]',

								children: ($$anchor, $$slotProps) => {
									var fragment_3 = $.comment();
									var node_9 = $.first_child(fragment_3);

									{
										var consequent_1 = ($$anchor) => {
											var fragment_4 = root_8();

											$.next();
											$.append($$anchor, fragment_4);
										};

										var alternate = ($$anchor) => {
											var text_3 = $.text('Create Split');

											$.append($$anchor, text_3);
										};

										$.if(node_9, ($$render) => {
											if ($.get(isLoading)) $$render(consequent_1); else $$render(alternate, false);
										});
									}

									$.append($$anchor, fragment_3);
								},

								$$slots: { default: true }
							});

							$.reset(form);

							$.event('submit', form, (e) => {
								e.preventDefault();
								handleCreateSplit();
							});

							$.append($$anchor, form);
						},

						$$slots: { default: true }
					});
				});

				$.append($$anchor, fragment_1);
			},

			$$slots: { default: true }
		});
	});

	$.reset(div_1);

	var node_10 = $.sibling(div_1, 2);

	{
		var consequent_2 = ($$anchor) => {
			var div_3 = root_10();
			var node_11 = $.child(div_3);

			$.component(node_11, () => Card.Root, ($$anchor, Card_Root_1) => {
				Card_Root_1($$anchor, {
					class: 'border-teal-300 bg-teal-50',

					children: ($$anchor, $$slotProps) => {
						var fragment_5 = root_11();
						var node_12 = $.first_child(fragment_5);

						$.component(node_12, () => Card.Header, ($$anchor, Card_Header_1) => {
							Card_Header_1($$anchor, {
								class: 'pb-2',

								children: ($$anchor, $$slotProps) => {
									var fragment_6 = $.comment();
									var node_13 = $.first_child(fragment_6);

									$.component(node_13, () => Card.Title, ($$anchor, Card_Title_1) => {
										Card_Title_1($$anchor, {
											class: 'text-base',

											children: ($$anchor, $$slotProps) => {
												$.next();

												var text_4 = $.text('Recent splits');

												$.append($$anchor, text_4);
											},

											$$slots: { default: true }
										});
									});

									$.append($$anchor, fragment_6);
								},

								$$slots: { default: true }
							});
						});

						var node_14 = $.sibling(node_12, 2);

						$.component(node_14, () => Card.Content, ($$anchor, Card_Content_1) => {
							Card_Content_1($$anchor, {
								class: 'space-y-2',

								children: ($$anchor, $$slotProps) => {
									var fragment_7 = $.comment();
									var node_15 = $.first_child(fragment_7);

									$.each(node_15, 17, () => $.get(recentSplits), (split) => split.id, ($$anchor, split) => {
										var div_4 = root_15();
										var span = $.child(div_4);
										var text_5 = $.child(span, true);

										$.reset(span);

										var node_16 = $.sibling(span, 2);

										Button(node_16, {
											size: 'sm',
											onclick: () => handleResume($.get(split).id),
											class: 'shrink-0 min-h-[36px]',

											children: ($$anchor, $$slotProps) => {
												$.next();

												var text_6 = $.text('Resume');

												$.append($$anchor, text_6);
											},

											$$slots: { default: true }
										});

										var button = $.sibling(node_16, 2);

										button.__click = () => handleDismiss($.get(split).id);
										$.reset(div_4);
										$.template_effect(() => $.set_text(text_5, $.get(split).name));
										$.append($$anchor, div_4);
									});

									$.append($$anchor, fragment_7);
								},

								$$slots: { default: true }
							});
						});

						$.append($$anchor, fragment_5);
					},

					$$slots: { default: true }
				});
			});

			$.reset(div_3);
			$.append($$anchor, div_3);
		};

		$.if(node_10, ($$render) => {
			if ($.get(recentSplits).length > 0) $$render(consequent_2);
		});
	}

	var node_17 = $.sibling(node_10, 4);

	{
		var consequent_3 = ($$anchor) => {
			var section = root_17();
			var button_1 = $.sibling($.child(section), 2);

			button_1.__click = () => clearToken();
			$.reset(section);
			$.append($$anchor, section);
		};

		$.if(node_17, ($$render) => {
			if (LAUNCH_MODE === 'DEVELOPMENT') $$render(consequent_3);
		});
	}

	$.reset(div);
	$.append($$anchor, fragment);
	$.pop();
}

$.delegate(['click']);
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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